import { useRetailer } from "@faire/deprecated-retailer-visitor-shared/hooks/retailer/useRetailer";
import { useOpenWithFaireApplicationStatus } from "@faire/deprecated-retailer-visitor-shared/stores/domain/OpenWithFaire/useOpenWithFaireApplicationStatus";
import { useOpenWithFaireTerms } from "@faire/deprecated-retailer-visitor-shared/stores/domain/OpenWithFaire/useOpenWithFaireTerms";
import { useCreditSummary } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useCreditSummary";
import { PotentialCreditBar } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/components/ApplicationStatus/PotentialCreditBar";
import { Core } from "@faire/design-tokens";
import { Check } from "@faire/slate/icons/Check";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { IRetailerLaunchWithFaire } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaire";
import { formatMoney, moneyOrZero } from "@faire/web/common/money";
import { MoneyMath } from "@faire/web/common/MoneyMath";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { SlideIn } from "@faire/web/ui/SlideIn/SlideIn";
import React from "react";
import styled from "styled-components";

const POTENTIAL_CREDIT_BAR_STATUSES: Array<
  keyof typeof IRetailerLaunchWithFaire.Status | undefined
> = [
  IRetailerLaunchWithFaire.Status.ON_HOLD,
  IRetailerLaunchWithFaire.Status.ON_HOLD_NO_LEASE,
  IRetailerLaunchWithFaire.Status.APPLIED,
];

export const ApplicationStatusCard: React.FC = () => {
  const { status } = useOpenWithFaireApplicationStatus();
  const { retailer } = useRetailer();
  const termsOrZero = moneyOrZero(retailer?.terms_limit);
  const { creditSummary } = useCreditSummary();
  const availableTermsLimit = creditSummary?.available_credit;
  const availableTermsOrZero = moneyOrZero(availableTermsLimit);

  const showPotentialCreditBar = POTENTIAL_CREDIT_BAR_STATUSES.includes(status);

  return (
    <SlideIn offset="50px">
      <ColumnCard>
        <Typography variant="paragraphSansMedium">
          <CardHeader
            termsOrZero={termsOrZero}
            availableTermsOrZero={availableTermsOrZero}
          />
        </Typography>
        <HeaderSpacer
          termsOrZero={termsOrZero}
          availableTermsOrZero={availableTermsOrZero}
        />
        <CardBody
          termsOrZero={termsOrZero}
          availableTermsOrZero={availableTermsOrZero}
        />
        <Spacer height={4} />
        <Typography variant="labelSansRegular">
          <CardLabel
            termsOrZero={termsOrZero}
            availableTermsOrZero={availableTermsOrZero}
          />
        </Typography>
        {showPotentialCreditBar ? <PotentialCreditBar /> : null}
      </ColumnCard>
    </SlideIn>
  );
};

interface ITermsInfo {
  termsOrZero: IFaireMoney;
  availableTermsOrZero: IFaireMoney;
}
const CardHeader: React.FC<ITermsInfo> = ({ availableTermsOrZero }) => {
  const { status } = useOpenWithFaireApplicationStatus();
  const { retailerName } = useRetailer();

  switch (status) {
    case IRetailerLaunchWithFaire.Status.REJECTED:
      if (MoneyMath.greaterThanZero(availableTermsOrZero)) {
        return <>{retailerName}</>;
      }

      return null;
    case IRetailerLaunchWithFaire.Status.ON_HOLD:
    case IRetailerLaunchWithFaire.Status.ON_HOLD_NO_LEASE:
    case IRetailerLaunchWithFaire.Status.APPLIED:
    case IRetailerLaunchWithFaire.Status.ACCEPTED:
    default:
      return <>{retailerName}</>;
  }
};

const HeaderSpacer: React.FC<ITermsInfo> = ({
  availableTermsOrZero,
  termsOrZero,
}) => {
  const { status } = useOpenWithFaireApplicationStatus();

  switch (status) {
    case IRetailerLaunchWithFaire.Status.REJECTED:
      if (MoneyMath.greaterThanZero(availableTermsOrZero)) {
        return <Spacer height="7x" />;
      }

      return null;
    case IRetailerLaunchWithFaire.Status.ON_HOLD:
    case IRetailerLaunchWithFaire.Status.ON_HOLD_NO_LEASE:
    case IRetailerLaunchWithFaire.Status.APPLIED:
      if (MoneyMath.greaterThanZero(termsOrZero)) {
        return <Spacer height="5x" />;
      }

      return <Spacer height="7x" />;
    case IRetailerLaunchWithFaire.Status.ACCEPTED:
    default:
      return <Spacer height="7x" />;
  }
};

const CardBody: React.FC<ITermsInfo> = ({
  availableTermsOrZero,
  termsOrZero,
}) => {
  const { status } = useOpenWithFaireApplicationStatus();
  const { offeredMaxTerms } = useOpenWithFaireTerms();
  switch (status) {
    case IRetailerLaunchWithFaire.Status.REJECTED:
      if (MoneyMath.greaterThanZero(availableTermsOrZero)) {
        return <CardMoney money={availableTermsOrZero} />;
      }

      return (
        <Column gap={12}>
          <Typography variant="paragraphSansMedium">
            <StrictLocalMsg
              id="HeevaX"
              defaultMessage="Enjoy other benefits of Faire"
              description={{
                text: "Faire has rejected a retailer's application for a payment terms increase",
                img: "https://cdn.faire.com/fastly/3b2bfe8aab25c0e595bd80bb444ec68b8143c008788a57011929775e96062685.png",
              }}
            />
          </Typography>
          <OtherFaireBenefitRow
            benefitText={
              <StrictLocalMsg
                id="a7fEi+"
                defaultMessage="Access to unique products"
                description={{
                  text: "Faire has rejected a retailer's application for a payment terms increase",
                  img: "https://cdn.faire.com/fastly/3b2bfe8aab25c0e595bd80bb444ec68b8143c008788a57011929775e96062685.png",
                }}
              />
            }
          />
          <OtherFaireBenefitRow
            benefitText={
              <StrictLocalMsg
                id="I9Mh/H"
                defaultMessage="Low minimums on thousands of brands"
                description={{
                  text: "Faire has rejected a retailer's application for a payment terms increase",
                  img: "https://cdn.faire.com/fastly/3b2bfe8aab25c0e595bd80bb444ec68b8143c008788a57011929775e96062685.png",
                }}
              />
            }
          />
          <OtherFaireBenefitRow
            benefitText={
              <StrictLocalMsg
                id="sRkAHs"
                defaultMessage="Free returns on opening orders"
                description={{
                  text: "Faire has rejected a retailer's application for a payment terms increase",
                  img: "https://cdn.faire.com/fastly/3b2bfe8aab25c0e595bd80bb444ec68b8143c008788a57011929775e96062685.png",
                }}
              />
            }
          />
          <OtherFaireBenefitRow
            benefitText={
              <StrictLocalMsg
                id="2KUvUF"
                defaultMessage="One, easy-to-use site for your buying"
                description={{
                  text: "Faire has rejected a retailer's application for a payment terms increase",
                  img: "https://cdn.faire.com/fastly/3b2bfe8aab25c0e595bd80bb444ec68b8143c008788a57011929775e96062685.png",
                }}
              />
            }
          />
        </Column>
      );
    case IRetailerLaunchWithFaire.Status.ACCEPTED:
      if (MoneyMath.greaterThanZero(termsOrZero)) {
        return <CardMoney money={termsOrZero} />;
      }

      return null;
    case IRetailerLaunchWithFaire.Status.ON_HOLD:
    case IRetailerLaunchWithFaire.Status.ON_HOLD_NO_LEASE:
    case IRetailerLaunchWithFaire.Status.APPLIED:
      return <CardMoney money={offeredMaxTerms} />;
    default:
      return null;
  }
};

const CardMoney: React.FC<{ money: IFaireMoney }> = ({ money }) => {
  return (
    <Typography variant="displayLSerifRegular">
      {formatMoney({ money: money, omitCentsIfZero: true })}
    </Typography>
  );
};

const CardLabel: React.FC<ITermsInfo> = ({
  availableTermsOrZero,
  termsOrZero,
}) => {
  const { status } = useOpenWithFaireApplicationStatus();

  switch (status) {
    case IRetailerLaunchWithFaire.Status.REJECTED:
      if (MoneyMath.greaterThanZero(availableTermsOrZero)) {
        return (
          <StrictLocalMsg
            id="idJmID"
            defaultMessage="Your available payment terms"
            description={{
              text: "Faire has accepted a retailer's application for a payment terms increase",
              img: "https://cdn.faire.com/fastly/0244328410970b0128eb47943aa4652e05adb3be2a9f956060c434d299c90076.png",
            }}
          />
        );
      }

      return null;
    case IRetailerLaunchWithFaire.Status.ACCEPTED:
      if (MoneyMath.greaterThanZero(termsOrZero)) {
        return (
          <StrictLocalMsg
            id="3T+tWG"
            defaultMessage="Your payment terms limit"
            description={{
              text: "Faire has accepted a retailer's application for a payment terms increase",
              img: "https://cdn.faire.com/fastly/c21dd046d223fcc9063cdae786bf725111c47fac96645a211e6cec0976c86be4.png",
            }}
          />
        );
      }

      return null;
    case IRetailerLaunchWithFaire.Status.ON_HOLD:
    case IRetailerLaunchWithFaire.Status.ON_HOLD_NO_LEASE:
    case IRetailerLaunchWithFaire.Status.APPLIED:
      return (
        <StrictLocalMsg
          id="7rMLmX"
          defaultMessage="Your potential payment terms"
          description={{
            text: "Faire has a retailer's application for a payment terms increase",
            img: "https://cdn.faire.com/fastly/12fc68a6df6bdc76a912e15fbec64f7a5eba35c1f33023117a04937b9090f7ef.png",
          }}
        />
      );
    default:
      return null;
  }
};

const OtherFaireBenefitRow: React.FC<{ benefitText: React.ReactNode }> = ({
  benefitText,
}) => {
  return (
    <Row gap={12}>
      <Check />
      <Typography>{benefitText}</Typography>
    </Row>
  );
};

const ColumnCard = styled(Column)`
  width: 100%;
  padding: ${getSpacing("3x")};
  border-radius: 16px;
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.15);
  background-color: ${Core.surface.inverse};

  ${responsiveMediaQueries.tabletAndAbove} {
    width: 360px;
  }
`;
